import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { BrowserRouter as Router } from "react-router-dom";
import { Provider } from "react-redux";
import { createBrowserHistory } from "history";
import mixpanel from "mixpanel-browser";

import store from "./store";

import { AuthProvider } from "./features/Auth";
import { PaymentModalProvider } from "./contexts/PaymentModalContext";
import App from "./components/AppContainer";
import UpdatePrompt from "components/UpdatePrompt";

import "./index.scss";

import { flagModelWithErrorForReview } from "utils/fileSharing/api";

const history = createBrowserHistory();

const sendFileForReview = () => {
    return {
        name: "sendFileForReview",
        processEvent(event, hint, client) {
            if (!event?.user?.email) return event;

            try {
                const url = event?.request?.url || "";
                if (url.includes("/models/")) {
                    let match = url.match(/\/models\/([^\/?#]+)/);
                    const modelId = match ? match[1] : null;
                    if (modelId) {
                        const errorMessage = event?.context?.feedback?.message || "";
                        flagModelWithErrorForReview({ modelId, userMessage: errorMessage });
                    }
                }
            } catch (err) {
                console.log("error sending file for review");
            }

            return event;
        },
    };
};

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
        Sentry.reactRouterV5BrowserTracingIntegration({ history }),
        Sentry.feedbackIntegration({
            autoInject: false,
            triggerLabel: "",
            colorScheme: "dark",
            isNameRequired: true,
            isEmailRequired: true,
            useSentryUser: { name: "name", email: "email" },
            themeDark: {
                background: "#262e3f",
                accentBackground: "#ac0d34",
                outline: "1px auto #f6f8f9",
            },
        }),
        Sentry.replayCanvasIntegration(),
        sendFileForReview(),
    ],
    tracesSampleRate: import.meta.env.DEV ? 0.0 : 1.0,
    environment: import.meta.env.DEV ? "development" : "production",
    attachStacktrace: true,
    replaysSessionSampleRate: import.meta.env.DEV ? 0.0 : 0.25,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 10,
    enabled: import.meta.env.PROD,
});

mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, { debug: false, autocapture: true });

const app = (
    <Provider store={store}>
        <AuthProvider>
            <PaymentModalProvider>
                <Router>
                    <App />
                    <UpdatePrompt />
                </Router>
            </PaymentModalProvider>
        </AuthProvider>
    </Provider>
);

ReactDOM.render(app, document.getElementById("root"));
